import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { listClasses, getStudentCounts, listStudents, searchStudents, updateStudent, createStudent, moveStudent, getStudentsCanEdit, listVirtualClasses, getVirtualClassMembers } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import ResizableTable from "../../components/ResizableTable";
import { AttendanceModal, ScoreModal, QuizExamModal, ExamListModal, QuizExamMatrixModal } from "./HappySchoolStudentActions";

// Webview nhúng trong Zalo/Messenger/Instagram/Line/WeChat CHỈ chặn tải file
// (Blob download) trên iOS (WKWebView giới hạn) — Android webview vẫn tải
// bình thường qua Download Manager của hệ thống nên không cần chặn.
function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}
function downloadWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Export lại."
    );
    return;
  }
  const wbArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const KHOI_ORDER = ["12", "11", "10"];
const gradeOf = (nm) => (String(nm).match(/^(\d+)/) || [])[1] || "";

// Sắp xếp tên kiểu Việt Nam: theo TÊN (chữ cuối) trước, rồi họ + đệm.
function vnNameParts(name) {
  const parts = String(name || "").trim().split(/\s+/);
  return { ten: parts[parts.length - 1] || "", ho: parts.slice(0, -1).join(" ") };
}
function compareVnName(a, b) {
  const pa = vnNameParts(a.name);
  const pb = vnNameParts(b.name);
  return pa.ten.localeCompare(pb.ten, "vi") || pa.ho.localeCompare(pb.ho, "vi");
}

const STUDENT_FIELDS = [
  ["name", "Họ tên"],
  ["dob", "Ngày sinh"],
  ["gender", "Giới tính"],
  ["personalId", "Mã định danh"],
  ["studentCode", "Mã học sinh"],
  ["phone", "SĐT học sinh"],
  ["classRole", "Chức vụ (Lớp trưởng/Lớp phó)"],
  ["status", "Trạng thái"],
  ["parent1Name", "Phụ huynh 1"],
  ["parent1Phone", "SĐT phụ huynh 1"],
  ["parent2Name", "Phụ huynh 2"],
  ["parent2Phone", "SĐT phụ huynh 2"],
  ["note", "Ghi chú"],
];

const STATUS_OPTIONS = ["Đang học", "Đã đăng ký điểm danh AI", "Lưu ban", "Bảo lưu", "Bỏ học", "Chuyển trường", "Nghỉ"];
// Danh sách chính chỉ hiện 2 trạng thái này; các trạng thái khác (kể cả trống) sang tab Ngừng học.
const isStudying = (st) => ["đang học", "đã đăng ký điểm danh ai"].includes(String((st && st.status) || "").normalize("NFC").trim().toLowerCase());

function StudentDetailModal({ schoolId, student, classId, className, onClose, onSaved, isNew = false, classes = [], onMoved }) {
  const [editing, setEditing] = useState(isNew);
  const [fields, setFields] = useState(() =>
    Object.fromEntries(STUDENT_FIELDS.map(([k]) => [k, student[k] || ""]))
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Chuyển lớp: bật form chọn lớp mới
  const [moving, setMoving] = useState(false);
  const [toClassId, setToClassId] = useState("");
  const doMove = async () => {
    const target = classes.find((c) => c.id === toClassId);
    if (!target) { setErr("Chọn lớp cần chuyển đến."); return; }
    if (!window.confirm(`Chuyển học sinh ${student.name} từ lớp ${className} sang lớp ${target.name}?`)) return;
    setSaving(true); setErr("");
    try {
      await moveStudent(schoolId, classId, student.id, toClassId);
      if (onMoved) onMoved(student, classId, toClassId, target.name);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  // Chỉ admin hoặc GV chủ nhiệm lớp này mới được sửa.
  const [canEdit, setCanEdit] = useState(isNew);
  useEffect(() => {
    if (isNew || !schoolId || !classId) return;
    getStudentsCanEdit(schoolId, classId).then((r) => setCanEdit(!!(r && r.canEdit))).catch(() => setCanEdit(false));
  }, [isNew, schoolId, classId]);

  const resetFields = () => setFields(Object.fromEntries(STUDENT_FIELDS.map(([k]) => [k, student[k] || ""])));

  const cancelEdit = () => {
    if (isNew) { onClose(); return; }
    resetFields();
    setErr("");
    setEditing(false);
  };

  const save = async () => {
    if (!fields.name.trim()) {
      setErr("Thiếu họ tên.");
      return;
    }
    if (isNew && !fields.status) {
      setErr("Chưa chọn trạng thái.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      if (isNew) {
        const created = await createStudent(schoolId, classId, fields);
        onSaved(created);
        return;
      }
      await updateStudent(schoolId, classId, student.id, fields);
      onSaved({ ...student, ...fields });
      setEditing(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={editing ? undefined : onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px 0" }}>{isNew ? "Thêm học sinh" : "Thông tin học sinh"}</h3>
        <p style={{ margin: "0 0 14px 0", color: "#6b7280", fontSize: 13 }}>Lớp {className}</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {STUDENT_FIELDS.map(([key, label]) => (
            <label key={key} style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ color: "#4b5563", fontWeight: 600 }}>{label}{isNew && (key === "name" || key === "status") && <span style={{ color: "#dc2626" }}> *</span>}</span>
              {editing && key === "status" ? (
                <select
                  value={fields[key]}
                  onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                  style={{ padding: 7, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, background: "#fff" }}
                >
                  <option value="">— Chưa chọn —</option>
                  {[...STATUS_OPTIONS, ...(fields[key] && !STATUS_OPTIONS.includes(fields[key]) ? [fields[key]] : [])].map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : editing ? (
                <input
                  value={fields[key]}
                  onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                  style={{ padding: 7, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }}
                />
              ) : (
                <div style={{ padding: "7px 8px", background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 6, minHeight: 18 }}>
                  {fields[key] || "—"}
                </div>
              )}
            </label>
          ))}
        </div>
        {moving && !editing && (
          <div style={{ marginTop: 14, padding: 12, border: "1px solid #99f6e4", background: "#f0fdfa", borderRadius: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Chuyển học sinh sang lớp khác</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 13 }}>Từ lớp <b>{className}</b> sang lớp:</span>
              <select value={toClassId} onChange={(e) => setToClassId(e.target.value)} style={{ padding: 7, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, background: "#fff" }}>
                <option value="">— Chọn lớp —</option>
                {classes.filter((c) => c.id !== classId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={doMove} disabled={saving || !toClassId} style={searchBtnStyle}>{saving ? "Đang chuyển..." : "Chuyển"}</button>
              <button type="button" onClick={() => { setMoving(false); setToClassId(""); setErr(""); }} style={clearBtnStyle}>Huỷ</button>
            </div>
          </div>
        )}
        {err && <p style={{ color: "#dc2626", marginTop: 10 }}>{err}</p>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          {editing ? (
            <>
              <button type="button" onClick={cancelEdit} style={clearBtnStyle}>Huỷ</button>
              <button type="button" onClick={save} disabled={saving} style={searchBtnStyle}>
                {saving ? "Đang lưu..." : isNew ? "Thêm" : "Lưu"}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} style={clearBtnStyle}>Đóng</button>
              {canEdit && !isNew && onMoved && !moving && <button type="button" onClick={() => setMoving(true)} style={clearBtnStyle}>🔀 Chuyển lớp</button>}
              {canEdit && <button type="button" onClick={() => setEditing(true)} style={searchBtnStyle}>Sửa</button>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const HappySchoolStudents = () => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState([]);
  const [counts, setCounts] = useState({});
  const [virtualClasses, setVirtualClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null); // {id, name, isVirtual?}
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchPid, setSearchPid] = useState("");
  const [searchClass, setSearchClass] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = không tìm kiếm
  const [searching, setSearching] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null); // { student, classId, className }
  const [addingStudent, setAddingStudent] = useState(false);
  const [canEditClass, setCanEditClass] = useState(false); // admin hoặc GVCN lớp đang mở
  const openingRef = useRef(null);
  const [action, setAction] = useState(null); // "attendance" | "score"
  const [matrixExam, setMatrixExam] = useState(null); // {id, title} — xem ma trận điểm 1 bài KT Quiz3
  const [showStopped, setShowStopped] = useState(false); // tab Ngừng học của lớp đang mở

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([listClasses(schoolId), getStudentCounts(schoolId, { activeOnly: true }), listVirtualClasses(schoolId).catch(() => [])]) // lớp ảo lỗi/không có quyền -> vẫn hiện lớp thật
      .then(([c, ct, vc]) => {
        setClasses(c.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })));
        setCounts(ct);
        setVirtualClasses(vc);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  const openClass = (c, isVirtual) => {
    setSelectedClass({ ...c, isVirtual });
    setShowStopped(false);
    setCanEditClass(false);
    openingRef.current = c.id;
    if (!isVirtual) getStudentsCanEdit(schoolId, c.id).then((r) => { if (openingRef.current === c.id) setCanEditClass(!!(r && r.canEdit)); }).catch(() => {});
    setLoadingStudents(true);
    (isVirtual ? getVirtualClassMembers(schoolId, c.id) : listStudents(schoolId, c.id))
      .then((list) => setStudents(list.slice().sort(compareVnName)))
      .catch((err) => setError(err.message))
      .finally(() => setLoadingStudents(false));
  };

  const classNameById = {};
  classes.forEach((c) => { classNameById[c.id] = c.name; });

  const activeStudents = students.filter(isStudying);
  const stoppedStudents = students.filter((st) => !isStudying(st));
  const shownStudents = showStopped ? stoppedStudents : activeStudents;

  const openStudent = (student, classId, className) => setEditingStudent({ student, classId, className });

  const exportClassExcel = () => {
    if (!selectedClass || !shownStudents.length) return;
    const header = STUDENT_FIELDS.map(([, label]) => label);
    const rows = shownStudents.map((st) => STUDENT_FIELDS.map(([key]) => st[key] || ""));
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HSSV");
    downloadWorkbook(wb, `HSSV_${selectedClass.name}${showStopped ? "_NgungHoc" : ""}.xlsx`);
  };

  const onStudentSaved = (updated) => {
    // Đổi trạng thái giữa Đang học <-> Ngừng học thì sĩ số (chỉ đếm Đang học) của lớp thay đổi.
    const old = students.find((s) => s.id === updated.id) || (searchResults || []).find((s) => s.id === updated.id);
    const cid = old && (old.classId || (selectedClass && !selectedClass.isVirtual ? selectedClass.id : null));
    if (old && cid && isStudying(old) !== isStudying(updated)) {
      setCounts((ct) => ({ ...ct, [cid]: Math.max(0, (Number(ct[cid]) || 0) + (isStudying(updated) ? 1 : -1)) }));
    }
    setStudents((list) => list.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
    setSearchResults((list) => (list ? list.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)) : list));
    // Không đóng modal — cho xem lại ở chế độ chỉ đọc sau khi lưu.
  };

  // Sau khi chuyển lớp: bỏ HS khỏi danh sách lớp cũ, cập nhật sĩ số 2 lớp, đóng form.
  const onStudentMoved = (student, fromClassId, toClassId, toClassName) => {
    setStudents((list) => list.filter((s) => s.id !== student.id));
    setSearchResults((list) => (list ? list.map((s) => (s.id === student.id ? { ...s, classId: toClassId } : s)) : list));
    if (isStudying(student)) setCounts((ct) => ({ ...ct, [fromClassId]: Math.max(0, (Number(ct[fromClassId]) || 0) - 1), [toClassId]: (Number(ct[toClassId]) || 0) + 1 }));
    setEditingStudent(null);
    window.alert(`Đã chuyển ${student.name} sang lớp ${toClassName}.`);
  };

  const onStudentCreated = (created) => {
    setStudents((list) => [...list, created].sort(compareVnName));
    if (isStudying(created)) setCounts((ct) => ({ ...ct, [selectedClass.id]: (Number(ct[selectedClass.id]) || 0) + 1 }));
    setAddingStudent(false);
  };

  const doSearch = (e) => {
    if (e) e.preventDefault();
    if (!searchName.trim() && !searchPid.trim() && !searchClass.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    searchStudents(schoolId, { name: searchName.trim(), personalId: searchPid.trim(), className: searchClass.trim() })
      .then((list) => setSearchResults(list.slice().sort(compareVnName)))
      .catch((err) => setError(err.message))
      .finally(() => setSearching(false));
  };

  const clearSearch = () => {
    setSearchName("");
    setSearchPid("");
    setSearchClass("");
    setSearchResults(null);
  };

  if (!schoolId) {
    return (
      <div style={{ padding: 24 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }

  const byKhoi = {};
  KHOI_ORDER.forEach((k) => (byKhoi[k] = []));
  classes.forEach((c) => {
    const g = gradeOf(c.name);
    if (byKhoi[g]) byKhoi[g].push(c);
  });
  const totalClasses = KHOI_ORDER.reduce((sum, k) => sum + byKhoi[k].length, 0);
  const totalStudents = KHOI_ORDER.reduce(
    (sum, k) => sum + byKhoi[k].reduce((s, c) => s + (counts[c.id] || 0), 0),
    0
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto", textAlign: "left" }}>
      <h2 style={{ margin: "0 0 4px 0" }}>HSSV Đang học</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>Chọn 1 lớp để xem danh sách, hoặc tìm học sinh theo Tên / Mã định danh (nhập cả 2 để lọc chính xác hơn).</p>

      <form onSubmit={doSearch} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "8px 0 16px" }}>
        <input value={searchName} onChange={(e) => setSearchName(e.target.value)} placeholder="Tìm theo Tên" style={searchInputStyle} />
        <input value={searchPid} onChange={(e) => setSearchPid(e.target.value)} placeholder="Tìm theo Mã định danh" style={searchInputStyle} />
        <input value={searchClass} onChange={(e) => setSearchClass(e.target.value)} placeholder="Tìm theo Lớp (vd: 12A1)" style={{ ...searchInputStyle, minWidth: 150 }} />
        <button type="submit" style={searchBtnStyle}>{searching ? "Đang tìm..." : "🔍 Tìm"}</button>
        {searchResults !== null && (
          <button type="button" onClick={clearSearch} style={clearBtnStyle}>Xoá tìm kiếm</button>
        )}
      </form>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}

      {searchResults !== null ? (
        <div>
          <h3 style={{ margin: "0 0 10px 0", fontSize: 16 }}>Kết quả tìm kiếm — {searchResults.length} học sinh</h3>
          {searchResults.length === 0 ? (
            <p style={{ color: "#6b7280" }}>Không tìm thấy học sinh nào.</p>
          ) : (
            <ResizableTable
              storageKey="hs-students-search"
              maxHeight="70vh"
              rows={searchResults}
              rowKey={(st) => st.id}
              columns={[
                { key: "stt", header: "STT", width: 55, align: "center", render: (st, i) => i + 1 },
                { key: "name", header: "Họ tên", width: 200, sticky: true, align: "left", render: (st) => (
                  <strong onDoubleClick={() => openStudent(st, st.classId, classNameById[st.classId])} title="Bấm đúp để xem/sửa" style={{ cursor: "pointer" }}>{st.name}</strong>
                ) },
                { key: "lop", header: "Lớp", width: 80, render: (st) => classNameById[st.classId] || "" },
                { key: "dob", header: "Ngày sinh", width: 110, render: (st) => st.dob || "" },
                { key: "gender", header: "Giới tính", width: 80, align: "center", render: (st) => st.gender || "" },
                { key: "status", header: "Trạng thái", width: 170, render: (st) => st.status || "" },
                { key: "personalId", header: "Mã định danh", width: 150, render: (st) => st.personalId || "" },
                { key: "phone", header: "SĐT học sinh", width: 120, render: (st) => st.phone || "" },
                { key: "parent1Name", header: "Phụ huynh 1", width: 170, render: (st) => st.parent1Name || "" },
                { key: "parent1Phone", header: "SĐT PH1", width: 120, render: (st) => st.parent1Phone || "" },
                { key: "parent2Name", header: "Phụ huynh 2", width: 170, render: (st) => st.parent2Name || "" },
                { key: "parent2Phone", header: "SĐT PH2", width: 120, render: (st) => st.parent2Phone || "" },
                { key: "note", header: "Ghi chú", width: 120, render: (st) => st.note || "" },
              ]}
            />
          )}
        </div>
      ) : loading ? (
        <p>Đang tải...</p>
      ) : (
        <div style={{ marginBottom: 20 }}>
          {KHOI_ORDER.map((k) => {
            const soLop = byKhoi[k].length;
            const khoiTotal = byKhoi[k].reduce((sum, c) => sum + (counts[c.id] || 0), 0);
            return (
              <div key={k} style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-start", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, minWidth: 130, flexShrink: 0, color: "#374151" }}>
                  Khối {k} ({soLop}-{khoiTotal})
                </span>
                {byKhoi[k].length === 0 ? (
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>(chưa có lớp)</span>
                ) : (
                  <div style={{ display: "flex", gap: 8, overflowX: "auto", flex: 1, paddingBottom: 4 }}>
                    {byKhoi[k].map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => openClass(c)}
                        style={selectedClass?.id === c.id ? chipActiveStyle : chipStyle}
                      >
                        {c.name} ({counts[c.id] || 0})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {virtualClasses.length > 0 && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-start", marginBottom: 8, paddingTop: 8, borderTop: "1px solid #e5e7eb" }}>
              <span style={{ fontSize: 13, fontWeight: 700, minWidth: 130, flexShrink: 0, color: "#374151" }}>
                Lớp ảo ({virtualClasses.length})
              </span>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", flex: 1, paddingBottom: 4 }}>
                {virtualClasses.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => openClass(c, true)}
                    style={selectedClass?.id === c.id ? chipActiveStyle : chipStyle}
                  >
                    {c.name} ({c.memberCount})
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 4, paddingTop: 8, borderTop: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Tổng: {totalClasses} lớp — {totalStudents} học sinh
            </span>
          </div>
        </div>
      )}

      {searchResults === null && selectedClass && (
        <div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "0 0 10px" }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>
              Lớp {selectedClass.name} — {showStopped ? `Ngừng học: ${stoppedStudents.length}` : `${activeStudents.length} học sinh`}
            </h3>
            {!showStopped && activeStudents.length > 0 && (
              <>
                <button type="button" onClick={() => setAction("attendance")} style={actionBtnStyle}>📋 Điểm danh</button>
                <button type="button" onClick={() => setAction("score")} style={actionBtnStyle}>📝 Nhập điểm kiểm tra</button>
                <button type="button" onClick={() => setAction("quizexam")} style={actionBtnStyle}>🧪 Mở bài kiểm tra</button>
                <button type="button" onClick={() => setAction("examlist")} style={actionBtnStyle}>📋 Danh sách bài kiểm tra</button>
                <button type="button" onClick={exportClassExcel} style={actionBtnStyle}>⬇ Export</button>
              </>
            )}
            {showStopped && stoppedStudents.length > 0 && (
              <button type="button" onClick={exportClassExcel} style={actionBtnStyle}>⬇ Export</button>
            )}
            {!selectedClass.isVirtual && canEditClass && (
              <button type="button" onClick={() => setAddingStudent(true)} style={actionBtnStyle}>➕ Thêm HS</button>
            )}
            <button type="button" onClick={() => setShowStopped((v) => !v)} style={showStopped ? stoppedActiveBtnStyle : actionBtnStyle}>
              {showStopped ? "← Đang học" : `⏸ Ngừng học (${stoppedStudents.length})`}
            </button>
          </div>
          {loadingStudents ? (
            <p>Đang tải danh sách...</p>
          ) : shownStudents.length === 0 ? (
            <p style={{ color: "#6b7280" }}>{showStopped ? "Lớp này không có học sinh ngừng học." : "Lớp này chưa có học sinh đang học."}</p>
          ) : (
            <ResizableTable
              storageKey="hs-students"
              maxHeight="70vh"
              rows={shownStudents}
              rowKey={(st) => st.id}
              columns={[
                { key: "stt", header: "STT", width: 55, align: "center", render: (st, i) => i + 1 },
                { key: "name", header: "Họ tên", width: 200, sticky: true, align: "left", render: (st) => (
                  <strong onDoubleClick={() => openStudent(st, selectedClass.isVirtual ? st.classId : selectedClass.id, selectedClass.isVirtual ? st.className : selectedClass.name)} title="Bấm đúp để xem/sửa" style={{ cursor: "pointer" }}>{st.name}</strong>
                ) },
                ...(selectedClass.isVirtual ? [{ key: "lop", header: "Lớp gốc", width: 90, render: (st) => st.className || "" }] : []),
                { key: "dob", header: "Ngày sinh", width: 110, render: (st) => st.dob || "" },
                { key: "gender", header: "Giới tính", width: 80, align: "center", render: (st) => st.gender || "" },
                { key: "status", header: "Trạng thái", width: 170, render: (st) => st.status || "" },
                { key: "personalId", header: "Mã định danh", width: 150, render: (st) => st.personalId || "" },
                { key: "phone", header: "SĐT học sinh", width: 120, render: (st) => st.phone || "" },
                { key: "parent1Name", header: "Phụ huynh 1", width: 170, render: (st) => st.parent1Name || "" },
                { key: "parent1Phone", header: "SĐT PH1", width: 120, render: (st) => st.parent1Phone || "" },
                { key: "parent2Name", header: "Phụ huynh 2", width: 170, render: (st) => st.parent2Name || "" },
                { key: "parent2Phone", header: "SĐT PH2", width: 120, render: (st) => st.parent2Phone || "" },
                { key: "note", header: "Ghi chú", width: 120, render: (st) => st.note || "" },
              ]}
            />
          )}
        </div>
      )}

      {addingStudent && selectedClass && !selectedClass.isVirtual && canEditClass && (
        <StudentDetailModal
          isNew
          schoolId={schoolId}
          student={{}}
          classId={selectedClass.id}
          className={selectedClass.name}
          onClose={() => setAddingStudent(false)}
          onSaved={onStudentCreated}
        />
      )}

      {editingStudent && (
        <StudentDetailModal
          schoolId={schoolId}
          student={editingStudent.student}
          classId={editingStudent.classId}
          className={editingStudent.className}
          onClose={() => setEditingStudent(null)}
          onSaved={onStudentSaved}
          classes={classes.slice().sort((a, b) => String(a.name).localeCompare(String(b.name), "vi", { numeric: true }))}
          onMoved={onStudentMoved}
        />
      )}

      {action === "attendance" && selectedClass && (
        <AttendanceModal schoolId={schoolId} classId={selectedClass.id} className={selectedClass.name} students={activeStudents} onClose={() => setAction(null)} />
      )}
      {action === "score" && selectedClass && (
        <ScoreModal schoolId={schoolId} classId={selectedClass.id} className={selectedClass.name} students={activeStudents} onClose={() => setAction(null)} />
      )}
      {action === "quizexam" && selectedClass && (
        <QuizExamModal schoolId={schoolId} classId={selectedClass.id} className={selectedClass.name} onClose={() => setAction(null)} />
      )}
      {action === "examlist" && selectedClass && (
        <ExamListModal
          schoolId={schoolId}
          classId={selectedClass.id}
          className={selectedClass.name}
          onClose={() => setAction(null)}
          onViewMatrix={(id, title) => setMatrixExam({ id, title })}
        />
      )}
      {matrixExam && selectedClass && (
        <QuizExamMatrixModal
          schoolId={schoolId}
          classId={selectedClass.id}
          className={selectedClass.name}
          examId={matrixExam.id}
          examTitle={matrixExam.title}
          students={activeStudents}
          onClose={() => setMatrixExam(null)}
        />
      )}
    </div>
  );
};

const stoppedActiveBtnStyle = { padding: "6px 12px", background: "#fef3c7", color: "#92400e", border: "1px solid #f59e0b", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 };
const actionBtnStyle = { padding: "6px 12px", background: "#fff", color: "#0f766e", border: "1px solid #0ea5a3", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 };

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 24, width: "min(680px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" };
const searchInputStyle = { padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, minWidth: 200 };
const searchBtnStyle = { padding: "7px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const clearBtnStyle = { padding: "7px 12px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer" };
const chipStyle = { padding: "5px 12px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 500, flexShrink: 0, whiteSpace: "nowrap" };
const chipActiveStyle = { ...chipStyle, background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3", fontWeight: 700 };

export default HappySchoolStudents;
